import { useEffect, useId, useRef, useState } from "react";

import { useEscapeKey } from "../../lib/useEscapeKey";

export function NumericFilterEditor({
  open,
  title,
  ariaLabel,
  value,
  onValueChange,
  onApply,
  onClose,
  isValid,
  invalidMessage,
  min,
  step,
  placeholder,
  suffix,
}: {
  open: boolean;
  title: string;
  ariaLabel: string;
  value: string;
  onValueChange: (next: string) => void;
  onApply: (next: number) => void;
  onClose: (restoreFocus: boolean) => void;
  isValid: (value: number) => boolean;
  invalidMessage: string;
  min: number;
  step: number;
  placeholder: string;
  suffix?: string;
}) {
  const titleId = useId();
  const inputId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [showError, setShowError] = useState(false);

  useEscapeKey(() => onClose(true), open);

  useEffect(() => {
    if (!open) return;
    setShowError(false);
    inputRef.current?.focus();
    function handlePointerDown(event: MouseEvent) {
      const target = event.target as Node;
      if (panelRef.current?.contains(target)) return;
      onClose(false);
    }
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [onClose, open]);

  if (!open) return null;

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label={ariaLabel}
      className="absolute left-0 z-40 mt-1 w-64 rounded-md border border-slate-200 bg-white p-3 shadow-lg"
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          const parsed = Number(value);
          if (!isValid(parsed)) {
            setShowError(true);
            return;
          }
          onApply(parsed);
        }}
      >
        <label
          id={titleId}
          htmlFor={inputId}
          className="block text-xs font-medium text-slate-700"
        >
          {title}
        </label>
        <div className="mt-2 flex items-center gap-2">
          <div className="relative">
            <input
              ref={inputRef}
              id={inputId}
              type="number"
              min={min}
              step={step}
              value={value}
              onChange={(event) => {
                onValueChange(event.target.value);
                setShowError(false);
              }}
              placeholder={placeholder}
              aria-invalid={showError}
              className="h-9 w-28 appearance-none rounded-md border border-slate-200 bg-white px-3 pr-7 text-sm tabular-nums outline-none focus:border-emerald-500 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
            {suffix ? (
              <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-slate-500">
                {suffix}
              </span>
            ) : null}
          </div>
          <button
            type="submit"
            className="h-9 rounded-md bg-emerald-600 px-3 text-sm font-medium text-white hover:bg-emerald-700"
          >
            Übernehmen
          </button>
        </div>
        {showError ? (
          <p role="alert" className="mt-2 text-xs text-rose-700">
            {invalidMessage}
          </p>
        ) : null}
        <button
          type="button"
          onClick={() => onClose(true)}
          className="mt-2 text-xs text-slate-500 hover:text-slate-900 hover:underline"
        >
          Abbrechen
        </button>
      </form>
    </div>
  );
}
